
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '@/context/LanguageContext';
import { useTranslation } from 'react-i18next';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  ogImage?: string;
  ogUrl?: string;
  ogType?: string;
  canonicalUrl?: string;
  localeAlternates?: boolean;
}

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  keywords,
  ogImage = "/og-image.webp",
  ogUrl,
  ogType = "website",
  canonicalUrl,
  localeAlternates = true,
}) => {
  const { language, availableLanguages } = useLanguage();
  const { t } = useTranslation();
  
  // Use translated SEO keywords if no custom keywords provided
  const seoKeywords = keywords || t('seo.keywords');
  
  // Construct full title with brand name
  const fullTitle = `${title} | ${t('app.title')}`;
  
  // Generate alternate links for different languages
  const alternateLinks = localeAlternates && ogUrl ? availableLanguages.map(lang => {
    // Replace language code in URL if present, or add it
    const langUrl = ogUrl.replace(/\/(en|zh)\//, `/${lang.code}/`);
    return (
      <link 
        key={lang.code} 
        rel="alternate" 
        hrefLang={lang.code} 
        href={langUrl} 
      />
    );
  }) : null;
  
  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <html lang={language} />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={seoKeywords} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:locale" content={language === 'zh' ? 'zh_CN' : 'en_US'} />
      {ogImage && <meta property="og:image" content={ogImage} />}
      {ogUrl && <meta property="og:url" content={ogUrl} />}
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      {ogImage && <meta name="twitter:image" content={ogImage} />}
      
      {/* Canonical URL */}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      
      {/* Alternate language versions */}
      {alternateLinks}
    </Helmet>
  );
};

export default SEO;
